import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Divider,
  Paper,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  type EditorChangeRequest,
  EditRelationOperation,
  type Path,
  type Relation,
  type RelationTemplate,
  type Relative,
  type TemplateId,
} from '../api/api'
import { PathRow } from './PathRow'
import { RelativeRow } from './RelativeRow'

type RelationEditorProps = {
  onChange: (changeRequests: EditorChangeRequest[]) => void
  relation: Relation
  templates: RelationTemplate[]
  requests: EditorChangeRequest[]
  showAllowedPaths?: boolean
}

export const RelationEditor = ({ relation, requests, templates, onChange, showAllowedPaths = true }: RelationEditorProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<RelationTemplate | null>(null)
  const [pathFrom, setPathFrom] = useState<Relative | null>(null)
  const [pathTo, setPathTo] = useState<Relative | null>(null)

  const resolvedRelatives = resolveRelatives(relation.relatives, requests)
  const resolvedAllowedPaths = resolveAllowedPaths(relation.allowedPaths, requests)

  const getConnectionsForRelative = (relativeId: TemplateId) => {
    const template = templates.find((template) => template.templateId === relativeId)
    return template?.connections
  }

  const handleAddTemplate = (template: RelationTemplate) => {
    const existingRemoveRequest = requests.find(
      (request) => request.op === EditRelationOperation.REMOVE && request.relative.id === template.templateId,
    )

    // Here we want to avoid looping change requests, check if there are any existing remove request for the same template, if so we remove it to get the same result
    if (existingRemoveRequest) {
      const filteredRequests = requests.filter((request) => request.id !== existingRemoveRequest.id)
      onChange(filteredRequests)

      return
    }

    const actionId = uuid()

    const updatedRequests = requests.concat([
      {
        id: actionId,
        op: EditRelationOperation.ADD,
        relative: {
          externalName: template.externalName,
          id: template.templateId,
          name: template.internalName,
          actionId: actionId,
        },
        type: 'relative',
      },
    ])

    onChange(updatedRequests)
  }

  const handleRemoveTemplate = (relative: RelativeWithActionId) => {
    // Similar here to the add template, avoid looping requests by checking if we have already added the template by looking at the action id.
    if (relative.actionId) {
      const filteredRequests = requests.filter((request) => request.id !== relative.actionId)
      onChange(filteredRequests)

      return
    }

    // We dont need to set an action id here since the add method will use the templateId instead
    const updatedRequests = requests.concat([
      {
        id: uuid(),
        op: EditRelationOperation.REMOVE,
        relative: relative,
        type: 'relative',
      },
    ])

    onChange(updatedRequests)
  }

  const handleAddPath = (from: TemplateId, to: TemplateId) => {
    // Avoid looping change requests, if there is a pending remove request for the same path we cancel it out.
    const existingRemoveRequest = requests.find(
      (request) => request.op === EditRelationOperation.REMOVE_ALLOWED_PATH && request.path.from === from && request.path.to === to,
    )

    if (existingRemoveRequest) {
      const filteredRequests = requests.filter((request) => request.id !== existingRemoveRequest.id)
      onChange(filteredRequests)

      return
    }

    const actionId = uuid()

    const updatedRequests = requests.concat([
      {
        id: actionId,
        op: EditRelationOperation.ADD_ALLOWED_PATH,
        path: { from, to, actionId },
        type: 'path',
      },
    ])

    onChange(updatedRequests)
  }

  const handleRemovePath = (path: PathWithActionId) => {
    // If the path was added in this editing session we simply cancel the add request instead of emitting a remove.
    if (path.actionId) {
      const filteredRequests = requests.filter((request) => request.id !== path.actionId)
      onChange(filteredRequests)

      return
    }

    const updatedRequests = requests.concat([
      {
        id: uuid(),
        op: EditRelationOperation.REMOVE_ALLOWED_PATH,
        path: { from: path.from, to: path.to },
        type: 'path',
      },
    ])

    onChange(updatedRequests)
  }

  const handleAddPathClick = () => {
    if (!pathFrom || !pathTo || pathFrom.id === pathTo.id) {
      return
    }

    handleAddPath(pathFrom.id, pathTo.id)
    setPathFrom(null)
    setPathTo(null)
  }

  const findRelative = (relativeId: TemplateId) => resolvedRelatives.find((relative) => relative.id === relativeId)

  const canAddPath = canAddAllowedPath(pathFrom, pathTo, resolvedAllowedPaths)

  const filteredTemplates = templates.filter((template) => !resolvedRelatives.some((relative) => relative.id === template.templateId))

  return (
    <Stack
      sx={{
        pb: 1,
        gap: 2,
      }}
    >
      <Autocomplete
        value={selectedTemplate}
        onChange={(_event, template) => {
          if (!template) {
            return
          }

          handleAddTemplate(template)
          setSelectedTemplate(null)
        }}
        options={filteredTemplates}
        getOptionLabel={(option) => option.internalName}
        renderInput={(params) => <TextField {...params} label="Add a template to the relation" />}
      />
      <RelativeListContainer>
        <Accordion
          defaultExpanded
          disableGutters
          sx={{ boxShadow: 'none', backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="templates-content" id="templates-header" sx={{ padding: 0 }}>
            <Typography variant="p2Medium">Templates ({resolvedRelatives.length})</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {resolvedRelatives.map((relative) => (
                <RelativeRow
                  key={relative.id}
                  relative={relative}
                  onRemove={(relative) => handleRemoveTemplate(relative)}
                  connections={getConnectionsForRelative(relative.id)}
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </RelativeListContainer>
      {showAllowedPaths && (
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Divider />
          <Typography variant="h2">Allowed paths</Typography>
          <Stack
            direction="row"
            sx={{
              gap: 2,
              alignItems: 'center',
            }}
          >
            <Stack
              sx={{
                flex: 1,
              }}
            >
              <Autocomplete
                fullWidth
                value={pathFrom}
                onChange={(_event, relative) => setPathFrom(relative)}
                options={resolvedRelatives}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="From" />}
              />
            </Stack>
            <Stack
              sx={{
                flex: 1,
              }}
            >
              <Autocomplete
                fullWidth
                value={pathTo}
                onChange={(_event, relative) => setPathTo(relative)}
                options={resolvedRelatives}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="To" />}
              />
            </Stack>
            <Stack
              sx={{
                flexBasis: 100,
              }}
            >
              <Button variant="contained" disabled={!canAddPath} onClick={handleAddPathClick}>
                Add path
              </Button>
            </Stack>
          </Stack>
          <RelativeListContainer>
            <Accordion
              defaultExpanded
              disableGutters
              sx={{ boxShadow: 'none', backgroundColor: 'transparent', '&:before': { display: 'none' } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="paths-content" id="paths-header" sx={{ padding: 0 }}>
                <Typography variant="p2Medium">Paths ({resolvedAllowedPaths.length})</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <Stack
                  sx={{
                    gap: 2,
                  }}
                >
                  {resolvedAllowedPaths.length === 0 ? (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      No allowed paths
                    </Typography>
                  ) : (
                    resolvedAllowedPaths.map((path) => (
                      <PathRow
                        key={`${path.from}-${path.to}`}
                        path={path}
                        fromRelative={findRelative(path.from)}
                        toRelative={findRelative(path.to)}
                        onRemove={handleRemovePath}
                      />
                    ))
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </RelativeListContainer>
        </Stack>
      )}
    </Stack>
  )
}

export const resolveRelatives = (relatives: Relative[], changeRequests: EditorChangeRequest[]): Relative[] => {
  if (changeRequests.length === 0) return relatives

  return changeRequests.reduce((acc, changeRequest) => {
    switch (changeRequest.op) {
      case EditRelationOperation.ADD:
        return [changeRequest.relative].concat(acc)
      case EditRelationOperation.REMOVE:
        return acc.filter((relative) => relative.id !== changeRequest.relative.id)
      default:
        return acc
    }
  }, relatives)
}

export const canAddAllowedPath = (from: Relative | null, to: Relative | null, existingPaths: Path[]): boolean => {
  if (!from || !to || from.id === to.id) {
    return false
  }

  const isDuplicate = existingPaths.some((path) => path.from === from.id && path.to === to.id)

  return !isDuplicate
}

export const resolveAllowedPaths = (paths: Path[], changeRequests: EditorChangeRequest[]): PathWithActionId[] => {
  return changeRequests.reduce<PathWithActionId[]>((acc, changeRequest) => {
    switch (changeRequest.op) {
      case EditRelationOperation.ADD_ALLOWED_PATH:
        return [changeRequest.path].concat(acc)
      case EditRelationOperation.REMOVE_ALLOWED_PATH:
        return acc.filter((path) => !(path.from === changeRequest.path.from && path.to === changeRequest.path.to))
      default:
        return acc
    }
  }, paths)
}

type RelativeWithActionId = Relative & { actionId?: string }
type PathWithActionId = Path & { actionId?: string }

const RelativeListContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[1],
}))

function uuid() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
  )
}
